import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Th, Td, Badge, EmptyState, StatCard } from '../components/ui';
import { fmtMoney, fmtDate, canWrite, canDelete } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const CATS = ['salary', 'transport', 'utilities', 'rent', 'raw_material', 'miscellaneous'];
const CAT_COLOR = {
  salary: 'bg-blue-50 text-blue-700 border-blue-200',
  transport: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  utilities: 'bg-amber-50 text-amber-700 border-amber-200',
  rent: 'bg-purple-50 text-purple-700 border-purple-200',
  raw_material: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  miscellaneous: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function Expenses() {
  const { user } = useAuth();
  const write = canWrite(user.role);
  const [expenses, setExpenses] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => api.get('/expenses').then((r) => setExpenses(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => { if (!window.confirm('Delete expense?')) return; await api.delete(`/expenses/${id}`); toast.success('Deleted'); load(); };
  const total = useMemo(() => expenses.reduce((s, e) => s + e.amount, 0), [expenses]);

  return (
    <div>
      <PageHeader title="Expenses" subtitle="Operating costs & payouts"
        actions={write && <Button onClick={() => setEdit('new')} data-testid="add-expense-btn"><Plus size={16} /> Expense</Button>} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Expenses" value={fmtMoney(total)} icon={Wallet} accent="text-rose-600" />
        {['salary', 'rent', 'raw_material'].map((cat) => (
          <StatCard key={cat} label={cat.replace('_', ' ')} value={fmtMoney(expenses.filter((e) => e.category === cat).reduce((s, e) => s + e.amount, 0))} />
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Date</Th><Th>Category</Th><Th>Description</Th><Th>Reference</Th><Th right>Amount</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50/60 transition-colors" data-testid="expense-row">
                  <Td>{fmtDate(e.date)}</Td>
                  <Td><Badge className={CAT_COLOR[e.category]}>{e.category.replace('_', ' ')}</Badge></Td>
                  <Td className="text-slate-700">{e.description}</Td>
                  <Td className="text-xs text-slate-400">{e.reference || '—'}</Td>
                  <Td right className="font-semibold">{fmtMoney(e.amount)}</Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {write && <button onClick={() => setEdit(e)} className="text-slate-400 hover:text-blue-600 p-1"><Pencil size={15} /></button>}
                    {canDelete(user.role) && <button onClick={() => del(e.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 size={15} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!expenses.length && <EmptyState title="No expenses logged" icon={Wallet} />}
      </Card>

      {edit && <ExpenseModal expense={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function ExpenseModal({ expense, onClose, onSaved }) {
  const [f, setF] = useState(expense || { category: 'miscellaneous', amount: 0, description: '', reference: '', date: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...f, amount: Number(f.amount) };
      if (expense) await api.put(`/expenses/${expense.id}`, payload);
      else await api.post('/expenses', payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={expense ? 'Edit Expense' : 'New Expense'} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-expense-btn">Save</Button></>}>
      <div className="space-y-4">
        <Select label="Category" value={f.category} onChange={(e) => set('category', e.target.value)} data-testid="expense-category">
          {CATS.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
        </Select>
        <Input label="Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="expense-amount" />
        <Input label="Description" value={f.description} onChange={(e) => set('description', e.target.value)} data-testid="expense-desc" />
        <Input label="Reference" value={f.reference} onChange={(e) => set('reference', e.target.value)} />
      </div>
    </Modal>
  );
}
